import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import SLABadge from "@/components/SLABadge";
import ComplaintTimeline from "@/components/ComplaintTimeline";
import { EvidenceViewer } from "@/components/EvidenceUploader";
import ComplaintMap from "@/components/ComplaintMap";
import { STATUS_LABELS, STATUS_COLORS, NOISE_SOURCES } from "@/lib/constants";
import { Volume2, AlertTriangle, CheckCircle2, XCircle, ClipboardCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function ComplaintDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/complaints/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id]);

  if (data === null) return <Layout><div className="p-10">Loading...</div></Layout>;
  if (data === false) return <Layout><div className="p-10">Not found</div></Layout>;

  const sourceLabel = NOISE_SOURCES.find((s) => s.key === data.noise_source)?.label || data.noise_source;
  const showMap = data.latitude != null && data.longitude != null;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="overline text-slate-500 mb-1">{data.reference}</div>
            <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="complaint-title">{data.title}</h1>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Badge variant="outline" className={STATUS_COLORS[data.status] || ""} data-testid="complaint-status">
                {STATUS_LABELS[data.status] || data.status}
              </Badge>
              <SLABadge
                createdAt={data.created_at}
                levelStartedAt={data.level_started_at}
                currentLevel={data.current_level}
                status={data.status}
                escalated={data.escalated}
              />
            </div>
          </div>

          <Card>
            <CardHeader><CardTitle className="font-display text-lg">Description</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{data.description}</p>
            </CardContent>
          </Card>

          {showMap && (
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg flex items-center gap-2">
                  Citizen location
                  {user?.role === "pi" && <span className="text-xs font-normal text-emerald-700 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200">Live for PI</span>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ComplaintMap lat={data.latitude} lng={data.longitude} address={data.address} reference={data.reference} />
              </CardContent>
            </Card>
          )}

          {data.status === "attended" && data.attended_remark && (
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg flex items-center gap-2 text-indigo-900">
                  <ClipboardCheck className="w-4 h-4" /> PI on-site remark
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
                  <div className="whitespace-pre-line">{data.attended_remark}</div>
                  <div className="text-xs text-indigo-700 mt-2">
                    Attended by {data.attended_by} · {new Date(data.attended_at).toLocaleString()} · SLA paused
                  </div>
                </div>
                {data.attend_photos?.length > 0 && (
                  <div className="mt-4">
                    <div className="overline text-slate-500 mb-2">On-site photos ({data.attend_photos.length})</div>
                    <EvidenceViewer evidence={data.attend_photos} />
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {data.status === "rejected" && data.rejected_reason && (
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg flex items-center gap-2 text-red-900">
                  <XCircle className="w-4 h-4" /> Rejected as fake complaint
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                  <div className="whitespace-pre-line">{data.rejected_reason}</div>
                  <div className="text-xs text-red-700 mt-2">
                    Rejected by {data.rejected_by} · {new Date(data.rejected_at).toLocaleString()}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="font-display text-lg">Evidence</CardTitle></CardHeader>
            <CardContent>
              <EvidenceViewer evidence={data.evidence} />
            </CardContent>
          </Card>

          {data.decibel_reading && (
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg flex items-center gap-2">
                  <Volume2 className="w-4 h-4" /> Decibel reading
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DecibelReadingView reading={data.decibel_reading} />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="font-display text-lg">Timeline</CardTitle></CardHeader>
            <CardContent>
              <ComplaintTimeline timeline={data.timeline || []} />
            </CardContent>
          </Card>

          {data.resolution_notes && (
            <Card>
              <CardHeader><CardTitle className="font-display text-lg">Resolution notes</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700 whitespace-pre-line">{data.resolution_notes}</p>
              </CardContent>
            </Card>
          )}

          {data.resolution_proofs?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg">Officer proof of action</CardTitle>
              </CardHeader>
              <CardContent>
                <EvidenceViewer evidence={data.resolution_proofs} />
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="font-display text-lg">Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Row label="Reference" value={data.reference} />
              <Row label="Zone" value={data.zone_name} />
              <Row label="Police Station" value={data.police_station_name} />
              <Row label="ACP" value={data.acp_name} />
              <Row label="Current Level" value={(data.current_level || "").toUpperCase()} />
              <Row label="Noise Source" value={sourceLabel} />
              <Row label="Address" value={data.address} />
              {data.latitude && <Row label="GPS" value={`${data.latitude.toFixed(5)}, ${data.longitude.toFixed(5)}`} />}
              {data.incident_datetime && <Row label="Incident Time" value={new Date(data.incident_datetime).toLocaleString()} />}
              <Row label="Filed by" value={data.is_anonymous ? "Anonymous" : data.citizen_name} />
              <Row label="Filed at" value={new Date(data.created_at).toLocaleString()} />
              {data.assigned_to_name && <Row label="Assigned to" value={data.assigned_to_name} />}
              {data.resolved_at && <Row label="Resolved at" value={new Date(data.resolved_at).toLocaleString()} />}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <div className="text-muted-foreground text-xs uppercase tracking-wider">{label}</div>
      <div className="text-right font-medium">{value || "—"}</div>
    </div>
  );
}

const CPCB_LIMITS = [
  { zone: "Silence zone", day: 50, night: 40 },
  { zone: "Residential", day: 55, night: 45 },
  { zone: "Commercial", day: 65, night: 55 },
  { zone: "Industrial", day: 75, night: 70 },
];

function DecibelReadingView({ reading }) {
  const peak = reading.peak_db || 0;
  const avg = reading.avg_db || 0;
  const capturedAt = reading.captured_at ? new Date(reading.captured_at).toLocaleString() : null;
  const worstExceed = CPCB_LIMITS.filter((r) => peak > r.day);
  const severity =
    peak >= 85 ? { label: "Severe", color: "bg-red-100 text-red-800 border-red-300" }
    : peak >= 70 ? { label: "High", color: "bg-amber-100 text-amber-900 border-amber-300" }
    : peak >= 55 ? { label: "Moderate", color: "bg-yellow-50 text-yellow-900 border-yellow-300" }
    : { label: "Low", color: "bg-emerald-50 text-emerald-900 border-emerald-300" };

  return (
    <div className="space-y-4" data-testid="decibel-reading-view">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Peak" value={peak} unit="dB" tone="red" testid="reading-peak" />
        <MetricCard label="Average" value={avg} unit="dB" tone="amber" testid="reading-avg" />
        <MetricCard label="Min" value={reading.min_db || 0} unit="dB" tone="slate" testid="reading-min" />
        <MetricCard label="Duration" value={reading.duration_sec || 0} unit="s" tone="slate" testid="reading-duration" />
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md border font-medium ${severity.color}`} data-testid="reading-severity">
          {peak >= 70 ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          {severity.label} noise level
        </span>
        {capturedAt && <span className="text-xs text-muted-foreground">Captured {capturedAt}</span>}
      </div>
      {worstExceed.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <div className="font-medium flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4" /> CPCB permissible limits breached
          </div>
          <div className="text-xs">
            Peak {peak} dB exceeds the daytime limit for: {worstExceed.map((r) => `${r.zone} (${r.day} dB)`).join(", ")}.
          </div>
        </div>
      )}
      {reading.samples?.length > 0 && (
        <div className="rounded-md border border-border bg-slate-50 p-3">
          <div className="overline text-slate-500 mb-2">Waveform (sampled every 250 ms)</div>
          <MiniWaveform samples={reading.samples} />
        </div>
      )}
      <div className="text-xs text-muted-foreground">
        Readings taken via an uncalibrated device microphone. Use alongside audio recording as supporting evidence.
      </div>
    </div>
  );
}

function MetricCard({ label, value, unit, tone = "slate", testid }) {
  const tones = {
    red: "bg-red-50 border-red-200 text-red-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  };
  return (
    <div className={`rounded-md border p-3 ${tones[tone] || tones.slate}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-display font-bold text-2xl tabular-nums mt-1">
        {value}<span className="text-xs opacity-70 ml-1">{unit}</span>
      </div>
    </div>
  );
}

function MiniWaveform({ samples }) {
  const max = 130;
  const min = 30;
  const w = 100;
  const step = w / Math.max(1, samples.length - 1);
  const points = samples.map((s, i) => {
    const x = (i * step).toFixed(2);
    const y = (100 - ((s.db - min) / (max - min)) * 100).toFixed(2);
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-16" data-testid="reading-waveform">
      <polyline fill="none" stroke="hsl(222 47% 20%)" strokeWidth="1" points={points} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
